(function () {
  'use strict';

  var TZ = 'Australia/Sydney';
  var WEEKDAY = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  var PROMPT_MS = 1400;
  var COVER_AFTER_LOAD_MS = 4000;
  var READY_FALLBACK_MS = 8000;
  var EMBED_ORIGIN = 'https://go.crisp.chat/chat/embed/';

  var isolationOpen = false;
  var framePainted = false;
  var coverDone = false;
  var gateDone = false;
  var chatRevealed = false;
  var shopIsOpen = true;
  var closeTimer = 0;
  var closeDone = true;
  var closeListener = null;
  var promptTimer = 0;
  var promptIndex = 0;
  var readyTimer = 0;
  var coverTimer = 0;
  var embedFrame = null;
  var vpBound = false;
  var vpPanel = null;
  var viewportPinEl = null;
  var viewportContent = '';
  var pageLockHeld = false;
  var scrollLockActive = false;
  var vvFrame = 0;

  function launcher() {
    return document.querySelector('[data-cdi-chat-launcher]');
  }

  function shell() {
    return document.querySelector('[data-cdi-chat-shell]');
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isNarrowViewport() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  }

  function isTouchPrimary() {
    return window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches;
  }

  function scheduleViewportSync() {
    if (vvFrame) return;
    vvFrame = window.requestAnimationFrame(function () {
      vvFrame = 0;
      syncShellViewport();
    });
  }

  function onVvResize() {
    scheduleViewportSync();
  }

  function onVvScroll() {
    scheduleViewportSync();
  }

  function onWinResize() {
    scheduleViewportSync();
  }

  function clearShellViewport(panel) {
    if (!panel) return;
    panel.style.removeProperty('--cdi-chat-keyboard-offset');
  }

  function syncShellViewport() {
    var panel = vpPanel || shell();
    if (!panel) return;
    var vp = window.visualViewport;
    if (!isNarrowViewport() || !vp) {
      clearShellViewport(panel);
      return;
    }
    if (vp.scale !== 1) {
      pinViewportScale();
    }
    var delta = window.innerHeight - vp.height;
    if (delta < 0) delta = 0;
    panel.style.setProperty('--cdi-chat-keyboard-offset', Math.round(delta) + 'px');
  }

  function pinViewportScale() {
    var pinned =
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
    if (viewportPinEl && viewportPinEl.parentNode) {
      viewportPinEl.setAttribute('content', pinned);
      return;
    }
    var original = document.querySelector('meta[name="viewport"]');
    if (!original || !original.parentNode) return;
    if (!viewportContent) {
      viewportContent = original.getAttribute('content') || '';
    }
    var next = document.createElement('meta');
    next.setAttribute('name', 'viewport');
    next.setAttribute('content', pinned);
    original.parentNode.insertBefore(next, original);
    original.parentNode.removeChild(original);
    viewportPinEl = next;
  }

  function restoreViewportScale() {
    var current = viewportPinEl || document.querySelector('meta[name="viewport"]');
    viewportPinEl = null;
    if (!current || !current.parentNode) {
      viewportContent = '';
      return;
    }
    if (!viewportContent) {
      viewportContent = '';
      return;
    }
    var restored = document.createElement('meta');
    restored.setAttribute('name', 'viewport');
    restored.setAttribute('content', viewportContent);
    current.parentNode.insertBefore(restored, current);
    current.parentNode.removeChild(current);
    viewportContent = '';
  }

  function lockPage() {
    if (pageLockHeld) return;
    pageLockHeld = true;
    scrollLockActive = isTouchPrimary();
    if (scrollLockActive) {
      if (window.CdiScrollLock) {
        window.CdiScrollLock.lock('chat');
      } else {
        document.documentElement.style.overflowY = 'hidden';
      }
      pinViewportScale();
    }
  }

  function unlockPage() {
    if (!pageLockHeld) return;
    pageLockHeld = false;
    if (scrollLockActive) {
      scrollLockActive = false;
      restoreViewportScale();
      if (window.CdiScrollLock) {
        window.CdiScrollLock.unlock('chat');
      } else {
        document.documentElement.style.overflowY = '';
      }
    }
  }

  function bindViewport(panel) {
    vpPanel = panel;
    syncShellViewport();
    if (vpBound) return;
    vpBound = true;
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onVvResize);
      window.visualViewport.addEventListener('scroll', onVvScroll);
    }
    window.addEventListener('resize', onWinResize);
  }

  function unbindViewport() {
    var panel = vpPanel || shell();
    vpBound = false;
    vpPanel = null;
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', onVvResize);
      window.visualViewport.removeEventListener('scroll', onVvScroll);
    }
    window.removeEventListener('resize', onWinResize);
    if (vvFrame) {
      window.cancelAnimationFrame(vvFrame);
      vvFrame = 0;
    }
    clearShellViewport(panel);
  }

  function forceOpenPreview() {
    try {
      return /(?:^|[?&])cdi_chat=open(?:&|$)/.test(window.location.search);
    } catch (err) {
      return false;
    }
  }

  function sydneyClock() {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ,
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23'
    }).formatToParts(new Date());
    var map = {};
    parts.forEach(function (part) {
      map[part.type] = part.value;
    });
    return {
      weekday: WEEKDAY[map.weekday] || 0,
      minutes: Number(map.hour) * 60 + Number(map.minute)
    };
  }

  function shopOpen(clock) {
    if (forceOpenPreview()) return true;
    if (clock.weekday >= 1 && clock.weekday <= 5) {
      return clock.minutes >= 9 * 60 && clock.minutes < 17 * 60 + 30;
    }
    if (clock.weekday === 6) {
      return clock.minutes >= 9 * 60 && clock.minutes < 15 * 60;
    }
    return false;
  }

  function stopPrompts(panel) {
    window.clearInterval(promptTimer);
    promptTimer = 0;
    promptIndex = 0;
    if (!panel) return;
    panel.querySelectorAll('[data-cdi-chat-prompt]').forEach(function (el) {
      el.classList.remove('is-active');
    });
  }

  function startPrompts(panel) {
    stopPrompts(panel);
    if (!panel || !shopIsOpen || chatRevealed) return;
    var items = panel.querySelectorAll('[data-cdi-chat-prompt]');
    if (!items.length) return;
    function tick() {
      items.forEach(function (el, index) {
        el.classList.toggle('is-active', index === promptIndex);
      });
      promptIndex = (promptIndex + 1) % items.length;
    }
    tick();
    if (prefersReducedMotion()) return;
    promptTimer = window.setInterval(tick, PROMPT_MS);
  }

  function applyHours(panel) {
    shopIsOpen = shopOpen(sydneyClock());
    panel.classList.toggle('is-after-hours', !shopIsOpen);
  }

  function finishClose(panel) {
    if (closeDone) return;
    closeDone = true;
    window.clearTimeout(closeTimer);
    if (panel && closeListener) {
      panel.removeEventListener('transitionend', closeListener);
    }
    closeListener = null;
    if (panel) {
      panel.classList.remove('is-closing');
      panel.setAttribute('aria-hidden', 'true');
    }
  }

  function setIsolationOpen(isOpen) {
    var button = launcher();
    var panel = shell();
    isolationOpen = isOpen;
    if (button) {
      button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
    if (!panel) return;

    if (isOpen) {
      closeDone = true;
      window.clearTimeout(closeTimer);
      if (closeListener) {
        panel.removeEventListener('transitionend', closeListener);
        closeListener = null;
      }
      panel.classList.remove('is-closing');
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      lockPage();
      bindViewport(panel);
      if (shopIsOpen && !chatRevealed) {
        startPrompts(panel);
      } else {
        stopPrompts(panel);
      }
      return;
    }

    stopPrompts(panel);
    unbindViewport();
    unlockPage();
    panel.classList.remove('is-open');
    if (prefersReducedMotion()) {
      panel.classList.remove('is-closing');
      panel.setAttribute('aria-hidden', 'true');
      return;
    }

    closeDone = false;
    panel.classList.add('is-closing');
    closeListener = function (event) {
      if (event.target !== panel) return;
      if (event.propertyName !== 'transform') return;
      finishClose(panel);
    };
    panel.addEventListener('transitionend', closeListener);
    closeTimer = window.setTimeout(function () {
      finishClose(panel);
    }, 400);
  }

  function markFramePainted(panel) {
    if (framePainted) return;
    framePainted = true;
    window.clearTimeout(readyTimer);
    readyTimer = 0;
    window.clearTimeout(coverTimer);
    coverTimer = window.setTimeout(function () {
      coverDone = true;
      maybeGate(panel);
    }, COVER_AFTER_LOAD_MS);
  }

  function maybeGate(panel) {
    if (gateDone || !panel) return;
    if (!framePainted || !coverDone) return;
    gateDone = true;
    window.clearTimeout(readyTimer);
    window.clearTimeout(coverTimer);
    readyTimer = 0;
    coverTimer = 0;
    panel.classList.add('is-frame-ready');
    revealChat(panel);
  }

  function revealChat(panel) {
    if (chatRevealed) return;
    chatRevealed = true;
    stopPrompts(panel);
    if (panel) {
      panel.classList.add('is-ready');
    }
  }

  function embedUrl(websiteId, email) {
    var url = EMBED_ORIGIN + '?website_id=' + encodeURIComponent(websiteId);
    if (email) {
      url += '&user_email=' + encodeURIComponent(email);
    }
    return url;
  }

  function mountEmbed(panel, websiteId, email) {
    var host = panel.querySelector('[data-cdi-chat-frame]');
    if (!host || embedFrame) return;
    var iframe = document.createElement('iframe');
    iframe.className = 'cdi-chat-shell__iframe';
    iframe.setAttribute('title', 'Chat');
    iframe.setAttribute('allow', 'camera; microphone');
    iframe.addEventListener('load', function () {
      markFramePainted(panel);
    });
    iframe.src = embedUrl(websiteId, email);
    host.appendChild(iframe);
    embedFrame = iframe;
    readyTimer = window.setTimeout(function () {
      framePainted = true;
      coverDone = true;
      maybeGate(panel);
    }, READY_FALLBACK_MS);
  }

  function openPanel(websiteId, email) {
    var panel = shell();
    if (!panel) return;
    applyHours(panel);
    if (!embedFrame) {
      panel.classList.remove('is-ready', 'is-frame-ready');
      framePainted = false;
      coverDone = false;
      gateDone = false;
      chatRevealed = false;
      mountEmbed(panel, websiteId, email);
    }
    setIsolationOpen(true);
  }

  function closePanel() {
    if (isolationOpen) {
      setIsolationOpen(false);
    }
  }

  function init() {
    var button = launcher();
    var panel = shell();
    if (!button || !panel) return;

    var websiteId = (button.getAttribute('data-cdi-chat-website-id') || '').trim();
    if (!websiteId) return;

    var email = (button.getAttribute('data-cdi-chat-user-email') || '').trim();
    var closeBtn = panel.querySelector('[data-cdi-chat-close]');

    button.addEventListener('click', function () {
      if (isolationOpen) {
        closePanel();
        return;
      }
      openPanel(websiteId, email);
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        closePanel();
      });
    }

    document.addEventListener('click', function (event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var opener = event.target.closest ? event.target.closest('[data-cdi-chat-open]') : null;
      if (!opener) return;
      event.preventDefault();
      openPanel(websiteId, email);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (isolationOpen) {
        closePanel();
      }
    });

    document.addEventListener('visibilitychange', function () {
      panel.classList.toggle('is-page-hidden', document.hidden);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
